import React, { useState } from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Pressable } from 'react-native-web';
import StaticMap from '../../StaticMap';
import { ClientProviderProfile } from '../../../../../api/ProfileClientView';

import {
  Absolute,
  BodyText,
  Button,
  HeaderText,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
  PortalOverlayModal,
  Stack,
} from '../../../../ui';
import {
  ArrowsChevronRightIcon,
  CallCallPhoneIcon,
  MessageIcon,
  TimeTimeIcon,
} from '../../../../ui/Icons';
import { openExternalUrl } from '../../../../../modules/openExternalUrl';
import analytics from '../../../../../modules/analytics';
import AboutMeModal from './AboutMeModal';
import { getProfileLocation } from '../../../../../api/ProfileClientView/ProfileClientView';
import { googleMapURL } from '../../../../../modules/maps/location';

type LocationActionModalProps = {
  profile: ClientProviderProfile;
  onMessageClicked: () => void;
  onClose: () => void;
};

const LocationActionModal: React.FC<LocationActionModalProps> = ({
  onClose, profile, onMessageClicked,
}) => {
  const businessHours = profile?.business_hours;
  const businessHoursSunday = businessHours[businessHours.length - 1];
  const newBusinessHours = [
    businessHoursSunday,
    ...businessHours.slice(0, businessHours.length - 1),
  ];
  const weekday = new Date().getDay();
  let closingString;
  if (newBusinessHours[weekday]?.end_time) {
    const splitClosingTime = newBusinessHours[weekday]?.end_time.split(':');
    const closingTime = new Date();
    closingTime.setHours(Number(splitClosingTime[0]));
    closingTime.setMinutes(Number(splitClosingTime[1]));
    const formattedTime = closingTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    closingString = `Open Until ${formattedTime}`;
  } else {
    closingString = 'Closed';
  }

  const handleCallProClicked = () => {
    openExternalUrl(`tel:${profile.public_phone_number}`);
  };

  const handleLocationModalCallProClicked = () => {
    analytics.track('location-action-modal-call-pro-clicked', {
      pro_id: profile.id,
    });
    handleCallProClicked();
  };

  const location = getProfileLocation(profile);
  const mapProfileInfoAvailable = !!location?.zipcode;
  const googleMapsLink = mapProfileInfoAvailable ? googleMapURL(location) : '';

  const handleGetDirectionsClicked = () => {
    analytics.track('location-action-modal-get-directions-clicked', {
      pro_id: profile.id,
    });
    openExternalUrl(googleMapsLink);
  };

  const [showAboutMe, setShowAboutMe] = useState(false);

  const handleOpenUntilClicked = () => {
    analytics.track('location-action-modal-open-until-clicked', {
      pro_id: profile.id,
    });
    setShowAboutMe(true);
  };

  const handleOpenUntilModalClose = () => {
    analytics.track('location-action-modal-open-until-closed', {
      pro_id: profile.id,
    });
    setShowAboutMe(false);
    analytics.track('location-action-modal-closed-after-open-until-closed', {
      pro_id: profile.id,
    });
    onClose();
  };

  const handleLocationActionModalCloseButtonClicked = () => {
    analytics.track('location-action-modal-close-button-clicked', {
      pro_id: profile.id,
    });
    onClose();
  };

  const handleLocationActionModalBackdropClicked = () => {
    analytics.track('location-action-modal-closed-by-backdrop-clicked', {
      pro_id: profile.id,
    });
    onClose();
  };

  const getProAddress = () => {
    if (profile.address1 && profile.address2 && profile.city && profile.state && profile.zipcode) {
      return `${profile.address1} ${profile.address2}, ${profile.city}, ${profile.state} ${profile.zipcode}`;
    }
    if (profile.address1 && profile.city && profile.state && profile.zipcode) {
      return `${profile.address1}, ${profile.city}, ${profile.state} ${profile.zipcode}`;
    }
    if (profile.city && profile.state && profile.zipcode) {
      return `${profile.city}, ${profile.state} ${profile.zipcode}`;
    }
    if (profile.zipcode) {
      return profile.zipcode;
    }
    return '';
  };

  const proAddress = getProAddress();

  const handleStaticMapPress = () => {
    analytics.track('location-action-modal-map-clicked', {
      pro_id: profile.id,
    });
  };

  const handleOnMessageClicked = () => {
    analytics.track('location_action_modal_message_button_clicked', {
      profile_type: 'provider',
      provider_id: profile.id,
    });

    onMessageClicked();
  };

  return (
    <PortalOverlay
      name="location-action-modal"
      onBackdropClick={handleLocationActionModalBackdropClicked}
    >
      <PortalOverlayModal
        position="menu"
        borderRadiusTop={32}
      >
        <PortalOverlayHeader
          alignItems="center"
          hideBottomBorder
          renderRightAction={() => (
            <Box
              paddingRight={24}
              paddingTop={24}
            >
              <PortalOverlayHeaderCloseBtn
                noPadding
                onCloseRequest={handleLocationActionModalCloseButtonClicked}
                testID="location_action_modal_close_button"
              />
            </Box>
          )}
        />
        <Stack paddingLeft={24} paddingRight={24}>
          <Box paddingTop={24}>
            <HeaderText
              level={1}
              testID="location_action_modal_pro_name"
            >
              {profile.name}
            </HeaderText>
          </Box>
          <Box paddingTop={16} paddingBottom={24}>
            <BodyText
              color={BrandColor.Metal}
              testID="location_action_modal_pro_address"
            >
              {proAddress}
            </BodyText>
          </Box>
          <Box
            maxHeight={160}
            minHeight={160}
            borderRadius={8}
            style={{
              overflow: 'hidden',
            }}
            testID="location_action_modal_location_map"
          >
            <StaticMap
              onPress={handleStaticMapPress}
              profile={profile}
              mapHeight={160}
            />
          </Box>
          <Box paddingTop={16}>
            <Pressable
              style={{ flex: 1, minWidth: 90 }}
              onPress={handleOpenUntilClicked}
              testID="location_action_modal_closing_time_button"
            >
              <Box
                alignItems="center"
                justifyContent="center"
                flex={1}
              >
                <Box
                  minWidth="100%"
                  borderRadius={8}
                  paddingX={16}
                  paddingY={16}
                  color={BrandColor.SuperLight}
                >
                  <Inline
                    space={16}
                  >
                    <TimeTimeIcon
                      color={BrandColor.Metal}
                      height={24}
                      width={24}
                    />
                    <BodyText
                      color={BrandColor.Metal}
                      appearance="normal"
                    >{closingString}
                    </BodyText>
                    <Absolute right={0}>
                      <ArrowsChevronRightIcon
                        color={BrandColor.Metal}
                        height={24}
                        width={24}
                      />
                    </Absolute>
                  </Inline>
                </Box>
              </Box>
            </Pressable>
          </Box>
          {showAboutMe && (
            <AboutMeModal
              profile={profile}
              onClose={handleOpenUntilModalClose}
              onMessagePro={onMessageClicked}
              onCallPro={handleCallProClicked}
            />
          )}
          <Box paddingTop={16}>
            <Inline space={8}>
              <Pressable
                style={{ flex: 1, minWidth: 90 }}
                onPress={handleOnMessageClicked}
                testID="location_action_modal_message_pro_button"
              >
                <Box
                  alignItems="center"
                  justifyContent="center"
                  flex={1}
                >
                  <Box
                    minWidth="100%"
                    borderRadius={8}
                    paddingX={16}
                    paddingY={16}
                    color={BrandColor.SuperLight}
                  >
                    <Inline
                      space={16}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <MessageIcon
                        color={BrandColor.Metal}
                        height={24}
                        width={24}
                      />
                      <BodyText
                        color={BrandColor.Metal}
                        appearance="normal"
                      >Message
                      </BodyText>
                    </Inline>
                  </Box>
                </Box>
              </Pressable>
              {profile.public_phone_number && (
                <Pressable
                  style={{ flex: 1, minWidth: 90 }}
                  onPress={handleLocationModalCallProClicked}
                  testID="location_action_modal_call_pro_button"
                >
                  <Box
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                  >
                    <Box
                      minWidth="100%"
                      borderRadius={8}
                      paddingX={16}
                      paddingY={16}
                      color={BrandColor.SuperLight}
                    >
                      <Inline
                        space={16}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <CallCallPhoneIcon
                          color={BrandColor.Metal}
                          height={24}
                          width={24}
                        />
                        <BodyText
                          color={BrandColor.Metal}
                          appearance="normal"
                        >Call
                        </BodyText>
                      </Inline>
                    </Box>
                  </Box>
                </Pressable>
              )}
            </Inline>
          </Box>
          <Box
            paddingTop={28}
            paddingBottom={40}
            borderRadius={4}
          >
            <Button
              onClick={handleGetDirectionsClicked}
              testID="location_action_modal_get_directions_button"
            >Get Directions
            </Button>
          </Box>
        </Stack>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};
export default LocationActionModal;
